import { formatCurrency, formatPercentage } from '@/lib/utils';
import { useDashboard, useDeals } from '@/lib/hooks';
import {
  TransactionStructure,
  transactionStructureLabels,
  transactionStructureIcons,
  DealStage,
  getDealPriorityColor,
  getDaysUntilClose,
} from '@/types/database';
import { Loader2, TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react';
import { useMemo } from 'react';
import { cn } from '@/lib/utils';

// Fund capacity configuration (would come from settings in production)
const FUND_CONFIG = {
  totalCapital: 25000000, // $25M total fund
  reservePercentage: 0.15, // Keep 15% in reserve
};

export function DashboardPage(): React.ReactElement {
  const { data, isLoading, error } = useDashboard();
  const { data: dealsData, isLoading: dealsLoading } = useDeals({ limit: 100 });

  const deals = dealsData?.data ?? [];

  // Calculate fund capacity metrics
  const fundMetrics = useMemo(() => {
    const totalCapital = FUND_CONFIG.totalCapital;
    const reserveAmount = totalCapital * FUND_CONFIG.reservePercentage;
    const deployableCapital = totalCapital - reserveAmount;

    // Capital committed = deals in progress (not closed, not archived)
    const activeDeals = deals.filter(d =>
      d.stage !== DealStage.CLOSED &&
      d.stage !== DealStage.ARCHIVED
    );
    const committedCapital = activeDeals.reduce((sum, d) => sum + d.target_value, 0);

    // Capital deployed = closed deals
    const closedDeals = deals.filter(d => d.stage === DealStage.CLOSED);
    const deployedCapital = closedDeals.reduce((sum, d) => sum + d.target_value, 0);

    // Available for new deals
    const availableCapital = deployableCapital - committedCapital - deployedCapital;

    // Utilization percentage
    const utilization = ((committedCapital + deployedCapital) / deployableCapital) * 100;

    return {
      totalCapital,
      reserveAmount,
      deployableCapital,
      committedCapital,
      deployedCapital,
      availableCapital,
      utilization,
      activeDealsCount: activeDeals.length,
      closedDealsCount: closedDeals.length,
    };
  }, [deals]);

  // Priority deals - sorted by closing probability
  const priorityDeals = useMemo(() => {
    return deals
      .filter(d => d.stage !== DealStage.CLOSED && d.stage !== DealStage.ARCHIVED)
      .sort((a, b) => (b.closing_probability ?? 50) - (a.closing_probability ?? 50))
      .slice(0, 5);
  }, [deals]);

  // Deals with concerns
  const concernDeals = useMemo(() => {
    return deals.filter(d =>
      d.concern_areas &&
      d.concern_areas.length > 0 &&
      d.stage !== DealStage.CLOSED &&
      d.stage !== DealStage.ARCHIVED
    );
  }, [deals]);

  if (isLoading || dealsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-negative font-medium">Failed to load dashboard data</p>
      </div>
    );
  }

  const stats = data ?? {
    activeDeals: 0,
    pipelineValue: 0,
    closedThisMonth: 0,
    avgTrueDiscount: 0,
    dealsByStructure: {} as Record<string, { count: number; value: number }>,
    recentActivity: [] as Array<{ id: string; action: string; deal_name: string | null; time: string }>,
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-text-primary">Dashboard</h2>
        <p className="text-text-secondary">Command center for your secondary market portfolio</p>
      </div>

      {/* Fund Capacity Management */}
      <div className="bg-primary-subtle rounded-lg border border-border p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-accent" />
          Fund Capacity Management
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-surface rounded-lg p-4 border border-border shadow-sm">
            <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">Total Capital</p>
            <p className="text-xl font-bold text-text-primary mt-1">{formatCurrency(fundMetrics.totalCapital)}</p>
          </div>
          <div className="bg-surface rounded-lg p-4 border border-border shadow-sm">
            <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">Committed</p>
            <p className="text-xl font-bold text-warning mt-1">{formatCurrency(fundMetrics.committedCapital)}</p>
            <p className="text-xs text-text-tertiary mt-1">{fundMetrics.activeDealsCount} active deals</p>
          </div>
          <div className="bg-surface rounded-lg p-4 border border-border shadow-sm">
            <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">Deployed</p>
            <p className="text-xl font-bold text-positive mt-1">{formatCurrency(fundMetrics.deployedCapital)}</p>
            <p className="text-xs text-text-tertiary mt-1">{fundMetrics.closedDealsCount} closed deals</p>
          </div>
          <div className="bg-surface rounded-lg p-4 border border-border shadow-sm">
            <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">Available</p>
            <p className={cn(
              'text-xl font-bold mt-1',
              fundMetrics.availableCapital > 0 ? 'text-primary' : 'text-negative'
            )}>
              {formatCurrency(fundMetrics.availableCapital)}
            </p>
          </div>
          <div className="bg-surface rounded-lg p-4 border border-border shadow-sm">
            <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">Utilization</p>
            <p className={cn(
              'text-xl font-bold mt-1',
              fundMetrics.utilization > 85 ? 'text-negative' :
              fundMetrics.utilization > 60 ? 'text-warning' : 'text-positive'
            )}>
              {fundMetrics.utilization.toFixed(0)}%
            </p>
            <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={cn(
                  'h-full rounded-full transition-all',
                  fundMetrics.utilization > 85 ? 'bg-negative' :
                  fundMetrics.utilization > 60 ? 'bg-warning' : 'bg-positive'
                )}
                style={{ width: `${Math.min(100, fundMetrics.utilization)}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-surface rounded-lg border border-border p-6 shadow-sm hover:shadow-md transition-shadow">
          <p className="text-sm font-medium text-text-secondary">Active Deals</p>
          <p className="text-3xl font-bold text-text-primary mt-2">{stats.activeDeals}</p>
        </div>
        <div className="bg-surface rounded-lg border border-border p-6 shadow-sm hover:shadow-md transition-shadow">
          <p className="text-sm font-medium text-text-secondary">Pipeline Value</p>
          <p className="text-3xl font-bold text-text-primary mt-2">
            {formatCurrency(stats.pipelineValue)}
          </p>
        </div>
        <div className="bg-surface rounded-lg border border-border p-6 shadow-sm hover:shadow-md transition-shadow">
          <p className="text-sm font-medium text-text-secondary">Closed This Month</p>
          <p className="text-3xl font-bold text-positive mt-2">{stats.closedThisMonth}</p>
        </div>
        <div className="bg-surface rounded-lg border border-border p-6 shadow-sm hover:shadow-md transition-shadow">
          <p className="text-sm font-medium text-text-secondary">Avg TRUE Discount</p>
          <p className="text-3xl font-bold text-accent mt-2">
            {stats.avgTrueDiscount ? formatPercentage(stats.avgTrueDiscount) : '-'}
          </p>
        </div>
      </div>

      {/* Priority Deals & Concerns Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Priority Deals */}
        <div className="bg-surface rounded-lg border border-border p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-positive" />
            Top Priority Deals
          </h3>
          {priorityDeals.length === 0 ? (
            <p className="text-text-tertiary text-center py-8">No active deals</p>
          ) : (
            <div className="space-y-3">
              {priorityDeals.map((deal) => {
                const priorityColor = getDealPriorityColor(deal);
                const daysUntilClose = getDaysUntilClose(deal);
                return (
                  <a
                    key={deal.id}
                    href={`/deals/${deal.id}`}
                    className={cn(
                      'block p-3 rounded-lg border-l-4 hover:shadow-sm transition-all',
                      priorityColor === 'green' ? 'border-l-positive bg-positive-subtle' :
                      priorityColor === 'yellow' ? 'border-l-warning bg-warning-subtle' :
                      'border-l-negative bg-negative-subtle'
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-text-primary text-sm">{deal.name}</p>
                        <p className="text-xs text-text-tertiary">{deal.seller_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-text-primary">{formatCurrency(deal.target_value)}</p>
                        <div className="flex items-center gap-2 text-xs mt-1">
                          <span className={cn(
                            'px-2 py-0.5 rounded-full font-semibold',
                            priorityColor === 'green' ? 'bg-positive text-white' :
                            priorityColor === 'yellow' ? 'bg-warning text-white' :
                            'bg-negative text-white'
                          )}>
                            {deal.closing_probability ?? 50}%
                          </span>
                          {daysUntilClose !== null && (
                            <span className="text-text-tertiary font-mono">{daysUntilClose}d</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          )}
        </div>

        {/* Deals with Concerns */}
        <div className="bg-surface rounded-lg border border-border p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-negative" />
            Deals Needing Attention
            {concernDeals.length > 0 && (
              <span className="bg-negative text-white text-xs font-medium px-2 py-0.5 rounded-full">
                {concernDeals.length}
              </span>
            )}
          </h3>
          {concernDeals.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="h-12 w-12 text-positive mx-auto mb-2" />
              <p className="text-text-tertiary">No deals with concerns</p>
            </div>
          ) : (
            <div className="space-y-3">
              {concernDeals.slice(0, 5).map((deal) => {
                const concerns = deal.concern_areas?.split(',').filter(Boolean) ?? [];
                return (
                  <a
                    key={deal.id}
                    href={`/deals/${deal.id}`}
                    className="block p-3 rounded-lg bg-negative-subtle border-l-4 border-l-negative hover:shadow-sm transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-text-primary text-sm">{deal.name}</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {concerns.map((concern, i) => (
                            <span key={i} className="text-xs bg-negative text-white px-2 py-0.5 rounded font-medium">
                              {concern.replace(/_/g, ' ')}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="text-right ml-3">
                        <p className="font-bold text-text-primary">{formatCurrency(deal.target_value)}</p>
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Transaction Structure Breakdown */}
      <div className="bg-surface rounded-lg border border-border p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Pipeline by Transaction Structure</h3>
        {Object.keys(stats.dealsByStructure ?? {}).length === 0 ? (
          <p className="text-text-tertiary text-center py-8">
            No deals yet. Run the Deal Sourcing workflow to find opportunities.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(TransactionStructure).map(([key, value]) => {
              const structureData = stats.dealsByStructure?.[value] ?? { count: 0, value: 0 };
              const percentage = stats.pipelineValue > 0
                ? (structureData.value / stats.pipelineValue) * 100
                : 0;
              return (
                <div key={key} className="border border-border rounded-lg p-4 bg-surface hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">{transactionStructureIcons[value]}</span>
                    <span className="font-semibold text-text-primary">
                      {transactionStructureLabels[value]}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-text-secondary">Deals:</span>
                      <span className="font-semibold text-text-primary">{structureData.count}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-text-secondary">Value:</span>
                      <span className="font-semibold text-text-primary">{formatCurrency(structureData.value)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-text-secondary">% of Pipeline:</span>
                      <span className="font-semibold text-accent">{percentage.toFixed(0)}%</span>
                    </div>
                  </div>
                  <div className="mt-3 h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full transition-all"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-surface rounded-lg border border-border p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Recent Activity</h3>
          {(stats.recentActivity?.length ?? 0) === 0 ? (
            <p className="text-text-tertiary text-center py-8">No recent activity</p>
          ) : (
            <div className="space-y-4">
              {stats.recentActivity?.map((activity) => (
                <div key={activity.id} className="flex items-start gap-3 pb-3 border-b border-border last:border-0">
                  <div className="w-2 h-2 mt-2 rounded-full bg-accent" />
                  <div className="flex-1">
                    <p className="text-sm text-text-primary">{activity.action}</p>
                    {activity.deal_name && (
                      <p className="text-sm text-text-tertiary">{activity.deal_name}</p>
                    )}
                  </div>
                  <span className="text-xs text-text-tertiary font-mono">
                    {new Date(activity.time).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-surface rounded-lg border border-border p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-3">
            <a href="/deals" className="p-4 border border-border rounded-lg hover:bg-surface-hover hover:shadow-sm transition-all text-left block">
              <span className="text-2xl mb-2 block">📄</span>
              <span className="font-semibold text-text-primary block">New LP Deal</span>
              <span className="text-sm text-text-secondary">Whole interest transfer</span>
            </a>
            <a href="/deals" className="p-4 border border-border rounded-lg hover:bg-surface-hover hover:shadow-sm transition-all text-left block">
              <span className="text-2xl mb-2 block">✂️</span>
              <span className="font-semibold text-text-primary block">Partial Strip</span>
              <span className="text-sm text-text-secondary">25-50% position</span>
            </a>
            <a href="/deals" className="p-4 border border-border rounded-lg hover:bg-surface-hover hover:shadow-sm transition-all text-left block">
              <span className="text-2xl mb-2 block">📦</span>
              <span className="font-semibold text-text-primary block">Multi-Fund</span>
              <span className="text-sm text-text-secondary">Bundle 3-5 funds</span>
            </a>
            <a href="/workflows" className="p-4 border border-border rounded-lg hover:bg-surface-hover hover:shadow-sm transition-all text-left block">
              <span className="text-2xl mb-2 block">🎯</span>
              <span className="font-semibold text-text-primary block">Run Sourcing</span>
              <span className="text-sm text-text-secondary">Find opportunities</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
